import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

import SlIcon from '/node_modules/@shoelace-style/shoelace/dist/react/icon';
import SlButton from '/node_modules/@shoelace-style/shoelace/dist/react/button';

import { Tooltip } from '@bigcommerce/big-design';

import { getSettings } from '../../redux/actions/settings';
import { useHelp } from '../partials/HelpContext';

const SECTIONS = [
  {
    items: [
      { url: '/', label: 'Home', icon: 'house', exact: true },
    ],
  },
  {
    title: 'Barcode Labels',
    items: [
      { url: '/manage', label: 'Print Labels', icon: 'printer' },
      { url: '/templates', label: 'Label Templates', icon: 'card-list' },
      { url: '/order_label_print', label: 'Print Order Labels', icon: 'receipt', badge: 'This tool is currently in beta.' },
      { url: '/print_wros', label: 'Print from WROs', icon: 'file-earmark-text', flag: 'hasSpoInstall', disabledText: 'Available for stores with the Supplier Purchase Orders app installed.', alsoMatch: ['/print_wro'] },
      { url: '/warehouse_labels', label: 'Warehouse Design Labels', icon: 'grid-3x3', flag: 'hasPpInstall', disabledText: 'Coming soon — available for stores with the Pack Perfect app installed.' },
    ],
  },
  {
    title: 'Orders',
    items: [
      { url: '/order', label: 'Create Order', icon: 'bag-check' },
      { url: '/pick', label: 'Pick + Verify', icon: 'box-seam' },
    ],
  },
  {
    title: 'Inventory',
    items: [
      { url: '/inventory/live', label: 'Live Modify', icon: 'lightning-charge' },
      { url: '/inventory/add', label: 'Add Inventory', icon: 'plus' },
      { url: '/inventory/update', label: 'Update Inventory', icon: 'pencil' },
      { url: '/inventory/remove', label: 'Reduce Inventory', icon: 'dash' },
      { url: '/update_results', label: 'Batch Changes', icon: 'bar-chart' },
    ],
  },
  {
    items: [
      { url: '/store_settings', label: 'App Settings', icon: 'gear' },
    ],
  },
];

const NavMenu = ({ payload, settings, loadSettings }) => {
  const location = useLocation();
  const { openHelp } = useHelp();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (settings.status === 'waiting') {
      loadSettings(payload);
    }
  }, []);

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.url;
    }

    if (location.pathname === item.url || location.pathname.startsWith(`${item.url}/`)) {
      return true;
    }

    return (item.alsoMatch || []).some((prefix) =>
      location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)
    );
  };

  const navItem = (item) => {
    const disabled = item.flag && !settings[item.flag];

    if (disabled) {
      return(
        <Tooltip
          key={item.url}
          placement="right"
          trigger={
            <span className="nav-item nav-item--disabled">
              <SlIcon name={item.icon} className="nav-item-icon" />
              <span>{item.label}</span>
            </span>
          }
        >
          {item.disabledText || 'Not available.'}
        </Tooltip>
      );
    }

    return(
      <Link
        key={item.url}
        to={item.url}
        className={`nav-item${isActive(item) ? ' nav-item--active' : ''}`}
        onClick={() => setDrawerOpen(false)}
      >
        <SlIcon name={item.icon} className="nav-item-icon" />
        <span>{item.label}</span>
        { item.badge &&
          <Tooltip
            placement="right"
            trigger={<SlIcon name="info-circle" className="nav-item-badge" />}
          >
            {item.badge}
          </Tooltip>
        }
      </Link>
    );
  }

  const helpItem = (
    <button
      type="button"
      className="nav-item nav-item--button"
      onClick={() => { setDrawerOpen(false); openHelp(); }}
    >
      <SlIcon name="question-circle" className="nav-item-icon" />
      <span>Help with this page</span>
    </button>
  );

  const supportItem = (
    <a href="mailto:support@athousandapps.com" className="nav-item">
      <SlIcon name="envelope" className="nav-item-icon" />
      <span>Get support</span>
    </a>
  );

  const sections = (
    <>
      {SECTIONS.map((section, index) =>
        <div className="nav-section" key={index}>
          { section.title &&
            <div className="nav-section-title">{section.title}</div>
          }
          {section.items.map(navItem)}
        </div>
      )}

      <div className="nav-section">
        <div className="nav-section-title">Help &amp; Support</div>
        {helpItem}
        {supportItem}
      </div>
    </>
  );

  return(
    <>
      <nav className="app-nav hide-mobile">
        {sections}
      </nav>

      <div className="nav-topbar only-mobile">
        <SlButton size="small" onClick={() => setDrawerOpen(true)}>
          <SlIcon name="list" slot="prefix" />
          Menu
        </SlButton>
      </div>

      { drawerOpen &&
        <div className="only-mobile">
          <div className="nav-overlay" onClick={() => setDrawerOpen(false)} />
          <nav className="nav-drawer">
            {sections}
          </nav>
        </div>
      }
    </>
  )
}

const mapStateToProps = state => ({
  settings: state.settings,
});

const mapDispatchToProps = (dispatch) => ({
  loadSettings: (payload) => dispatch(getSettings(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(NavMenu);
